import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { STORE_CONSTANTS } from '../enums/enums.ts';
import { AuthContextType, AuthUser } from '../types/Types.ts';
import { mockAPI } from '../api/mockApi.ts';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const user = localStorage.getItem(STORE_CONSTANTS.USER);
    if (user) {
      const parsedUser: AuthUser = JSON.parse(user);
      setUser(parsedUser);
    }
    setLoading(false);
  }, []);

  const login = async (
    emailOrUsername: string,
    password: string
  ): Promise<boolean> => {
    try {
      const { user, accessToken } = await mockAPI.login(
        emailOrUsername,
        password
      );

      const authUser: AuthUser = {
        email: user.email,
        username: user.username,
        token: accessToken,
      };

      localStorage.setItem(STORE_CONSTANTS.USER, JSON.stringify(authUser));
      setUser(authUser);

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setError('Invalid credentials');
      return false;
    }
  };

  const register = async (
    firstName: string,
    lastName: string,
    username: string,
    email: string,
    password: string
  ): Promise<boolean> => {
    try {
      const newUser = await mockAPI.register(
        firstName,
        lastName,
        username,
        email,
        password
      );

      return login(newUser.email, newUser.password);
    } catch (error) {
      console.error('Registration failed:', error);
      setError(error instanceof Error ? error.message : 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(STORE_CONSTANTS.USER);
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      if (!user?.token) {
        throw new Error('No token available');
      }

      const { accessToken } = await mockAPI.refreshToken(user.token);

      const updatedUser = { ...user, token: accessToken };
      localStorage.setItem(STORE_CONSTANTS.USER, JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Token refresh failed:', error);
      setError('Session expired. Please log in again.');
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, logout, refreshToken, loading, error }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
